import { Box, Flex, Stack } from '@chakra-ui/react';
import React from 'react';
import { useSelector } from 'react-redux';

import { makeGetShouldShowPreferredProviderTiers } from 'Containers/App/selectors';
import { ViewPlanPageCard } from 'Containers/ViewPlanPage/ViewPlanPageCard';
import { useProviderSelectionContext } from 'Contexts/providerSelectionContext';
import { useTextContext } from 'Contexts/textContext';
import { Button, Paragraph } from 'DesignLibrary/atoms';
import { IconBlockProps } from 'DesignLibrary/molecules/IconBlock';
import Text from 'Shared/Text';
import { DoctorProvidersProps } from 'Types/entities';
import { getPreferredNetworkProviderName } from 'Utils/helpers';
import { sendInsightsEvent } from 'Utils/insights';

import { ProviderSection } from './ProviderSection';

export const DoctorsProvidersCard = ({ planId, recommendation }: DoctorProvidersProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { filteredProviders, onOpenProviderModal, providerDetails } = useProviderSelectionContext();
  const shouldShowPreferredProviderTiers = useSelector(makeGetShouldShowPreferredProviderTiers());
  const { isMostPreferredEnabled, mostPreferredProviderName } = getPreferredNetworkProviderName(
    recommendation,
    shouldShowPreferredProviderTiers,
  );

  const title = retrieveContentfulData<string>(
    'health_details_section.plan_details.doctors_providers_card_title',
  );

  const icon: IconBlockProps = {
    icon: 'Stethoscope',
    color: 'blue',
  };

  const preferredNetworkProvider = filteredProviders[planId]
    ? filteredProviders[planId]?.preferredNetwork
    : [];
  const inNetworkProvider = filteredProviders[planId] ? filteredProviders[planId]?.inNetwork : [];
  const outOfNetworkProvider = filteredProviders[planId] ? filteredProviders[planId]?.outOfNetwork : [];

  const openProviderSelectionModal = () => {
    onOpenProviderModal();
    sendInsightsEvent(null, 'open_provider_search_modal');
  };

  return (
    <ViewPlanPageCard title={title} icon={icon}>
      {providerDetails?.length > 0 ? (
        <Stack gap={6} mt={6}>
          {shouldShowPreferredProviderTiers &&
            preferredNetworkProvider &&
            preferredNetworkProvider.length > 0 && (
              <ProviderSection
                networkType="preferred-network"
                providers={preferredNetworkProvider}
                isMostPreferredEnabled={isMostPreferredEnabled}
                mostPreferredProviderName={mostPreferredProviderName}
              />
            )}
          <ProviderSection networkType="in-network" providers={inNetworkProvider} />
          <ProviderSection networkType="out-of-network" providers={outOfNetworkProvider} />
          <Flex justify="flex-end">
            <Button
              size="small"
              onClick={openProviderSelectionModal}
              testId="provider-selection-update-button"
            >
              <Text field="health_section.secondary_content.provider_selection_update_button" />
            </Button>
          </Flex>
        </Stack>
      ) : (
        <Stack p={8} gap={3} bg="var(--background-gray)" align="center" justify="center" textAlign="center">
          <Paragraph size="small" data-testid="add-provider-text">
            <Text field="health_section.recommendation.in_network_providers_add_provider_text" />
          </Paragraph>
          <Box minW={['auto', 256]}>
            <Button
              iconLeft="Plus"
              testId="provider-selection-button"
              stretch
              size="small"
              onClick={openProviderSelectionModal}
            >
              <Text field="health_section.recommendation.in_network_providers_add_button" />
            </Button>
          </Box>
        </Stack>
      )}
    </ViewPlanPageCard>
  );
};
